import {
  all,
  call,
  debounce,
  getContext,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { SupabaseClient } from '@supabase/supabase-js';
import ddt from '@wix/da-ddt';
import {
  PapiDamzSearchAutocomplete,
  PapiDamzSearchMessages,
} from '@wix/da-papi-types';
import { BiData, MessageSearchBiEvent } from '@wix/da-bi/pkg/events';
import { Database } from '@wix/da-papi-types/lib/supabase';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import {
  isApiError,
  requestPuppy,
  XhrRequestOptions,
  XhrResponse,
} from '@wix/da-http-client';
import { FetchType } from '@wix/da-shared-react/pkg/Stream/base/types';
import { initializeIfNeeded } from '@wix/da-shared-react/pkg/Stream/base/actions';
import { StreamType, withCursor } from '@wix/da-shared-react/pkg/Stream';
import {
  fetchFailure,
  fetchSuccess,
  emptyStream,
  resetStream,
} from '@wix/da-shared-react/pkg/Stream/withCursor/actions';
import { takeStreamFetch } from '@wix/da-shared-react/pkg/Stream/helpers';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { isValidUsername } from '@wix/da-shared-react/pkg/User/helpers';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { messageSearchStreamId } from '../../constants';
import {
  PapiDamzSearchMessagesResult,
  PapiDamzSearchMessagesWithRoomData,
} from '../../types';
import { damzOnline } from '../actions/initialization';
import { getMessageSearchOrder, getSearchQuery } from '../selectors/search';
import {
  fetchAutocompleteSearch,
  fetchMessageSearchNext,
  fetchAutocompleteSearchSuccess,
  initMessageSearchStream,
  clearSearchQuery,
  closeSearchUI,
} from '../actions/search';
import { roomFetchToRoomItem, makePapiUser } from '../helpers';
import { getErrorTextFromResponse } from './helpers';
import {
  fakeFetchAutocompleteSearch,
  fakeFetchMessageSearchNext,
} from './search-fake-responses';
import { setDrawerFilter } from '../actions/drawer';
import i18next from 'i18next';

const logger = ddt.createLogger('damz.search');

const AUTOCOMPLETE_SEARCH_DEBOUNCE = 500;

// todo: remove when more solid (I guess?)
const USE_FAKE_AUTOCOMPLETE = false;
const USE_FAKE_MESSAGE_SEARCH = false;

// ---- main/autocomplete search
function* handleFetchAutocompleteSearch(
  action: ReturnType<typeof fetchAutocompleteSearch>
) {
  const q = action.payload.q;
  // currently just autocompleting users
  const shouldAutocomplete = isValidUsername(q);
  logger.log('handleFetchAutocompleteSearch', { q, shouldAutocomplete });
  if (!shouldAutocomplete) {
    return;
  }

  const doFake = yield select(getConfigKey, 'fakeApiRequests');
  let response: XhrResponse<PapiDamzSearchAutocomplete>;
  try {
    response =
      doFake || USE_FAKE_AUTOCOMPLETE
        ? yield call(fakeFetchAutocompleteSearch)
        : yield call(
            requestPuppy,
            { url: 'search/autocomplete', params: { q } },
            undefined,
            'damz'
          );
  } catch (e) {
    logger.error('handleFetchAutocompleteSearch failed', e);
    yield put(addErrorNotification(i18next.t('damz:damz.search.autocomplete.error')));
    return;
  }
  const results = (response as PapiDamzSearchAutocomplete)?.results;

  if (results) {
    // we're trying to maintain the snake_case to make backend values more traceable
    // so convert roomId back to room_id here
    const rekeyed = results.map(({ user }) => ({
      user: { user: user?.user, room_id: user?.roomId },
    }));
    yield put(fetchAutocompleteSearchSuccess(rekeyed));
  } else {
    const errorText = getErrorTextFromResponse(
      'fetchActiveParticipants',
      response
    );
    const params = { q };
    logger.error('error fetching participants:', errorText, params, response);
    yield put(addErrorNotification(errorText));
  }
}

// ---- keyword/messages search
function* handleInitMessageSearchStream(
  action: ReturnType<typeof initMessageSearchStream>
) {
  yield put(resetStream(messageSearchStreamId));
  yield put(
    initializeIfNeeded(messageSearchStreamId, {
      streamType: StreamType.WITH_CURSOR,
      autoFetch: true,
      streamParams: {},
      itemsPerFetch: 20,
      hasMore: true,
      items: [],
      fetchNextCallback: fetchMessageSearchNext,
    })
  );
  yield put(fetchMessageSearchNext(messageSearchStreamId));
}

function* handleFetchMessageSearchNext() {
  const { q, cursor, order } = yield select(state => ({
    q: getSearchQuery(state),
    order: getMessageSearchOrder(state),
    cursor: withCursor.selectors.getCursor(state, messageSearchStreamId),
  }));
  if (!q) {
    yield put(emptyStream(messageSearchStreamId));
    return;
  }

  yield call(
    logBiEvent,
    BiData<MessageSearchBiEvent>({
      evid: 486,
      search_terms: q,
    })
  );

  const doFake = yield select(getConfigKey, 'fakeApiRequests');
  if (doFake || USE_FAKE_MESSAGE_SEARCH) {
    return yield call(
      fakeFetchMessageSearchNext,
      addRoomDataToMessageSearchResults
    );
  }

  // this is an escape-hatch'ed version of fetchStreamContentsSupabase expanded
  // here bc we need a second fetch inside getStreamData - which is not able to
  // be a generator
  const fetchType = FetchType.Next;
  const options = {
    method: 'get',
    url: 'search/messages',
    params: { q, cursor, order },
    enableCancellation: true,
  } as XhrRequestOptions;

  try {
    logger.log('Fetching stream', messageSearchStreamId, options.params);
    const response: XhrResponse<PapiDamzSearchMessages> = yield call(
      requestPuppy,
      options,
      undefined,
      'damz'
    );

    if (!response || isApiError(response)) {
      logger.error('Error response', messageSearchStreamId, response);
      yield put(
        fetchFailure({
          streamId: messageSearchStreamId,
          fetchType,
          errorMsg: response ? response.error : 'Failed to fetch...',
        })
      );
      return;
    }

    logger.log('Successful fetch', q, messageSearchStreamId, response);
    const combinedResults =
      response.results.length > 0
        ? yield call(
            addRoomDataToMessageSearchResults,
            response.results as PapiDamzSearchMessagesResult[]
          )
        : [];
    yield put(
      fetchSuccess({
        streamId: messageSearchStreamId,
        hasMore: !!response.nextCursor,
        cursor: response.nextCursor,
        items: combinedResults,
      })
    );
  } catch (e) {
    logger.error('Error caught while fetching', e);
    yield put(
      fetchFailure({
        streamId: messageSearchStreamId,
        fetchType,
        errorMsg: 'Failed to fetch...',
      })
    );
  }
}

function* addRoomDataToMessageSearchResults(
  messageSearchResults: PapiDamzSearchMessagesResult[]
) {
  // precondition: no undefined messageIds
  // postcondition: order is preserved
  const messageIds = messageSearchResults?.map(result => result.messageId);
  // todo: remove if the rest works
  logger.log('addRoomDataToMessageSearchResults, messageIds:', messageIds);
  if (!messageIds?.length) {
    return [];
  }

  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');
  // TODO: any
  const response = yield client.rpc(
    'get_rooms_and_senders_by_message_id' as any,
    {
      p_message_ids: messageIds,
    }
  );
  if (!response || isApiError(response)) {
    throw response?.error ?? 'Failed to get_rooms_and_senders_by_message_id';
  }

  const messageIdToRoomMap = {};
  for (const row of response.data) {
    const { message_id, ...data } = row;
    // todo: remove if the rest works
    logger.log('addRoomDataToMessageSearchResults, builder:', message_id, row);
    messageIdToRoomMap[message_id] = {
      // we're using an array => array function on single pieces of data
      // so excuse the extra bracket confusion here
      roomItem: roomFetchToRoomItem([data])[0],
      user: makePapiUser(
        data.sender_username,
        data.sender_user_id,
        0,
        data.sender_usericon,
        data.sender_symbol,
        data.sender_banned
      ),
    };
    // todo: remove if the rest works
    logger.log(
      'addRoomDataToMessageSearchResults, builder mapped:',
      messageIdToRoomMap[message_id]
    );
  }

  const combinedData: PapiDamzSearchMessagesWithRoomData[] = [];
  for (const result of messageSearchResults) {
    if (result?.messageId && messageIdToRoomMap[result.messageId]) {
      const { roomItem, user } = messageIdToRoomMap[result.messageId];
      // todo: remove if the rest works
      logger.log('addRoomDataToMessageSearchResults, picker:', {
        roomItem,
        user,
      });
      roomItem && combinedData.push({ roomItem, user, ...result });
      // todo: remove if the rest works
      logger.log(
        'addRoomDataToMessageSearchResults, picker picked:',
        combinedData[combinedData.length - 1]
      );
    } else {
      logger.error(
        'message result without messageId or not found in get_rooms_by_message_id:',
        result
      );
    }
  }

  // this will prob be useful for a while
  const messageIdToRoomMapIds = Object.keys(messageIdToRoomMap);
  if (
    messageSearchResults.length !== messageIdToRoomMapIds.length ||
    messageIdToRoomMapIds.length !== combinedData.length
  ) {
    logger.error('possible issue or mismatch in message results:', {
      messageSearchResults,
      messageIdToRoomMap,
      combinedData,
    });
  }

  // todo: remove if the rest works
  logger.log('combinedData', combinedData);
  return combinedData;
}

function* emptyMessageSearchStream() {
  // don't keep stale results in mem
  yield put(resetStream(messageSearchStreamId));
}

function* handleFilterChange(action: ReturnType<typeof setDrawerFilter>) {
  const { filter } = action.payload;
  // don't keep stale results in mem
  if (filter !== 'all') {
    yield put(closeSearchUI());
  }
}

/** Handles autocomplete/header search and message/sidebar search */
export function* searchSaga() {
  // wait for online to happen
  yield take(damzOnline);
  yield all([
    debounce(
      AUTOCOMPLETE_SEARCH_DEBOUNCE,
      fetchAutocompleteSearch,
      handleFetchAutocompleteSearch
    ),
    takeEvery(initMessageSearchStream, handleInitMessageSearchStream),
    takeStreamFetch(fetchMessageSearchNext, handleFetchMessageSearchNext),
    takeLatest(clearSearchQuery, emptyMessageSearchStream),
    takeLatest(closeSearchUI, emptyMessageSearchStream),
    takeEvery(setDrawerFilter, handleFilterChange),
  ]);
}
